import { MIME_TYPES } from '@mantine/dropzone';
import { AnyAction, ThunkAction } from '@reduxjs/toolkit';
import { SessionHistory } from './components/NeoLeo/NeoLeoHistoryIndex';
import {
  BorrowerDto,
  ChecklistTaskResponse,
  LoanDto,
} from './openapi/atlantis';
import { DropboxResponse } from './openapi/dropbox';
import {
  IdentitySummaryResponse,
  JwtAuthenticationResponse,
} from './openapi/keymaker';
import { Message, Session } from './openapi/leo';
import { ChecklistResponse } from './openapi/sherlock';
import {
  ApplicationProgressResponse,
  ContentItem,
  QuestionAnswersResponse,
  UserPublicInfo,
  UserResponse,
} from './openapi/yenta';
import rootReducer from './slices/rootReducer';
import store from './slices/store';
import {
  CallSupportDetails,
  ContextualPrompt,
  FollowUpItem,
  LeoResponseMessageContext,
  LeoShareablesConfig,
} from './utils/LeoUtils';

export type EnumMap<E extends string, T> = {
  [key in E]: T;
};

export type PartialEnumMap<E extends string, T> = {
  [key in E]?: T;
};

export interface Mapping<T> {
  [key: string]: T | undefined;
}

export type RootState = ReturnType<typeof rootReducer>;

export interface ReduxStateFactory<T> {
  getState(state: Partial<T>): T;
}

export type AppDispatch = typeof store.dispatch;

export interface DropboxState {
  dropboxByBorrowerId: Record<string, DropboxResponse | undefined>;
}

export interface ApplicationState {
  currentQuestion: ContentItem | undefined;
  progress: ApplicationProgressResponse | undefined;
  loanProgress: ChecklistResponse | undefined;
  isCollapsed: boolean;
  questionAnswers: QuestionAnswersResponse | undefined;
}

export interface AuthState {
  loadingAuthUserDetail: boolean;
  authUserDetail: UserResponse | undefined;
  keymakerCurrentUser: IdentitySummaryResponse | undefined;
  jwtAuthResponseCurrentUser: JwtAuthenticationResponse | undefined;
  userById: Mapping<UserPublicInfo>;
  appCompatible: boolean;
}

export interface LoanState {
  userId: string | undefined;
  loans: LoanDto[] | undefined;
  loanDetail: LoanDto | undefined;
  borrowerDetail: BorrowerDto | undefined;
  checklists: ChecklistTaskResponse[] | undefined;
  checklistDetail: ChecklistTaskResponse | undefined;
  toggleChat: boolean;
}

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  AnyAction
>;

export interface ISelectOption<
  Label extends string = string,
  Value extends any = any,
> {
  label: Label;
  value: Value;
  disabled?: boolean;
}

export type ToastNotificationType = 'info' | 'warning' | 'success' | 'error';

export interface ToastNotificationData {
  type: ToastNotificationType;
  title: string;
  subtitle?: string;
}

export interface ToastNotificationState {
  visible: boolean;
  toastData: ToastNotificationData | undefined;
}

export enum ErrorCode {
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  SERVER_ERROR = 'SERVER_ERROR',
}

export interface ApiError {
  debugMessage?: string;
  message?: string;
  status: number;
  subErrors?: number;
  timestamp?: string;
  reportable?: boolean;
}

export interface ApiErrorResponse {
  'com.real.commons.apierror.ApiError': ApiError;
}

export enum AnalyticsEventEnum {
  WELCOME_SCREEN_VIEWED = 'welcome_screen_viewed',
  LOGIN_SCREEN_VIEWED = 'login_screen_viewed',
  SIGNUP_SCREEN_VIEWED = 'signup_screen_viewed',
  FORGOT_PASSWORD_SCREEN_VIEWED = 'forgot_password_screen_viewed',
  CHANGE_PASSWORD_SCREEN_VIEWED = 'change_password_screen_viewed',
  SECTION_INTRO_VIEWED = 'section_intro_viewed',
  QUESTION_ANSWERED = 'question_answered',
  APPLICATION_PROGRESS_PANEL_VIEWED = 'application_progress_panel_viewed',
  LEO_MESSAGE_HISTORY_CLICK = 'leo_message_history_click',
  LEO_BAR_CLICK = 'leo_bar_click',
  LEO_PROMPT_CLICK = 'leo_prompt_click',
  LEO_MESSAGE_SEND_CLICK = 'leo_message_send_click',
  LEO_RESPONSE_LINK_CLICK = 'LEO_RESPONSE_LINK_CLICK',
  LEO_RESPONSE_THUMBS_UP_CLICK = 'LEO_RESPONSE_THUMBS_UP_CLICK',
  LEO_RESPONSE_THUMBS_DOWN_CLICK = 'LEO_RESPONSE_THUMBS_DOWN_CLICK',
  LEO_RESPONSE_COPY_CLICK = 'LEO_RESPONSE_COPY_CLICK',
  // Onboarding milestone events
  ONBOARDING_APPLICATION_STARTED = 'onboarding_application_started',
  ONBOARDING_APPLICATION_FORM_SUBMITTED = 'onboarding_application_form_submitted',
  ONBOARDING_ICA_SIGNED = 'onboarding_ICA_signed',
  ONBOARDING_PAYMENT_SUBMITTED = 'onboarding_payment_submitted',
  // Self-serve team onboarding events
  SELF_SERVE_TEAM_INVITE_CLICKED = 'self_serve_team_invite_clicked',
  SELF_SERVE_ONBOARDING_APPLICATION_STARTED = 'self_serve_onboarding_application_started',
  SELF_SERVE_ONBOARDING_APPLICATION_FORM_SUBMITTED = 'self_serve_onboarding_application_form_submitted',
  SELF_SERVE_ONBOARDING_TEAM_ADDENDUM_SIGNED = 'self_serve_onboarding_team_addendum_signed',
  // Agent partner page events
  AGENT_PARTNER_PAGE_VIEWED = 'agent_partner_page_viewed',
  AGENT_PARTNER_GET_STARTED_CLICKED = 'agent_partner_get_started_clicked',
}

export type ThenArg<T> = T extends PromiseLike<infer U> ? U : T;

export enum themeEnum {
  GEMINI = 'gemini',
  RESKIN = 'reskin',
}

export interface ThemeState {
  currentTheme: themeEnum;
}

export enum InvitationType {
  EMAIL = 'EMAIL',
  LINK = 'LINK',
}

export interface PaymentState {
  promoDiscountInfo: any | null; // AmountDiscountResponse
  isApplyingPromo: boolean;
  isProcessingPayment: boolean;
  lastPaymentResponse: any | null; // ApplicationResponse
  invitationCouponCode: string | null;
  isInvitationDiscount: boolean;
  invitationId: string | null;
  invitationType: InvitationType | null;
}

export interface meResponseData {
  isLoginSuccess: boolean;
  userData?: UserResponse;
}

export enum SignatureWidgetModeEnum {
  SIGN = 'sign',
  VIEW = 'view',
  PREVIEW = 'preview',
}

export enum IFrameEventEnum {
  DOCUMENT_SIGNED = 'DOCUMENT_SIGNED',
  DOCUMENT_ALREADY_SIGNED = 'DOCUMENT_ALREADY_SIGNED',
  DOWNLOAD_DOCUMENT = 'DOWNLOAD_DOCUMENT',
  OPEN_DOCUMENT_IN_BROWSER = 'OPEN_DOCUMENT_IN_BROWSER',
}

export interface IFrameEvent {
  type: IFrameEventEnum;
  data: any;
}

export interface OnDocumentAlreadySignedEventData {}

export interface OnDocumentSignedEventData {
  documentId: string;
}

export enum ConfigDrivenIntroTemplate {
  TEMPLATE_1 = 'TEMPLATE_1',
  TEMPLATE_2 = 'REZEN_TC_1',
  TEMPLATE_3 = 'REZEN_AGENT_1',
}

export enum ConfigDrivenQuestionTemplate {
  TEMPLATE_1 = 'TEMPLATE_1',
  TEMPLATE_2 = 'REZEN_TC_1',
  TEMPLATE_3 = 'REZEN_AGENT_1',
}

export enum ConfigDrivenOutroTemplate {
  TEMPLATE_1 = 'TEMPLATE_1',
  TEMPLATE_2 = 'REZEN_TC_1',
  TEMPLATE_3 = 'REZEN_AGENT_1',
}

export interface LeoState {
  isNeoLeoOpen: boolean;
  isNeoLeoExpanded: boolean;
  openNeoLeo: boolean;
  loading: boolean;
  isSessionLoading: boolean;
  isChatLoading: boolean;
  isSessionError: boolean;
  session: SessionWithDisclaimer | undefined;
  showIntro: boolean;
  chatHistory: SessionHistory[];
  isHistoryLoading: boolean;
  currentChat: CurrentChat[];
  deletingSessionId: string | undefined;
  isLeoGenerating: boolean;
  isLeoThinking: boolean;
  isTicketCreated: boolean;
  msgContext: LeoResponseMessageContext | undefined;
  selectedItems: FollowUpItem[];
  isLeoModalVisible: boolean;
  isNudgeEnabled: boolean;
  defaultPrompts: ContextualPrompt[];
  callSupportDetails: CallSupportDetails | undefined;
  isCreatingTicket: boolean;
  isLatestLeoResponseExtensive: boolean;
  shareables: LeoShareablesConfig;
  showShareablesModal: boolean;
}

export interface SessionWithDisclaimer extends Session {
  id: string;
  disclaimer?: string;
}

export enum SenderReceiverEnum {
  SYSTEM = 'system',
  USER = 'user',
}

export interface CurrentChat extends Omit<Message, 'receiver' | 'sender'> {
  id: string;
  session_id: string;
  receiver?: SenderReceiverEnum;
  sender: SenderReceiverEnum;
}

export interface FormFieldTooltipIndexProps {
  tooltipIndex?: number;
}

export type MimeType = (typeof MIME_TYPES)[keyof typeof MIME_TYPES];

export enum SessionStorageKeyEnum {
  REZEN_CAMPAIGN = 'REZEN_CAMPAIGN',
}
